// ICS (iCalendar) export/import — NO OAuth, file-based interchange only (§4.9).
// Export a BIDIT calendar Gmail/Outlook accept; import an exported Outlook/Gmail
// file (events become view-only; re-import updates rather than duplicates).

function pad(n) { return String(n).padStart(2, '0'); }

/** Format a Date (or ISO string) as an ICS UTC timestamp: YYYYMMDDTHHMMSSZ */
export function toIcsDate(dateOrIso) {
  const d = new Date(dateOrIso);
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) + 'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) + 'Z'
  );
}

function escapeText(s = '') {
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function eventStart(e) {
  return e.start || e.startDate || e.date || e.time;
}
function eventEnd(e) {
  return e.end || e.endDate || e.start || e.startDate || e.date || e.time;
}

/** A stable UID so re-export/re-import doesn't duplicate (§4.9). */
export function eventUid(e) {
  return `${e.id || e.uid || Math.random().toString(36).slice(2)}@bidit`;
}

/** Build a full ICS document string from BIDIT events. */
export function eventsToIcs(events = [], calName = 'BIDIT') {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BIDIT//Calendar//EN',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeText(calName)}`,
  ];
  events.forEach((e) => {
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${eventUid(e)}`);
    lines.push(`DTSTAMP:${toIcsDate(new Date())}`);
    lines.push(`DTSTART:${toIcsDate(eventStart(e))}`);
    lines.push(`DTEND:${toIcsDate(eventEnd(e))}`);
    lines.push(`SUMMARY:${escapeText(e.title || 'Untitled')}`);
    if (e.location) lines.push(`LOCATION:${escapeText(e.location)}`);
    if (e.notes || e.description) lines.push(`DESCRIPTION:${escapeText(e.notes || e.description)}`);
    lines.push('END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/** Trigger a browser download of the ICS file. */
export function downloadIcs(events, filename = 'bidit-calendar.ics') {
  const ics = eventsToIcs(events);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseIcsDate(val = '') {
  // YYYYMMDDTHHMMSSZ or YYYYMMDD
  const m = val.match(/(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2}))?/);
  if (!m) return null;
  const [, y, mo, d, h = '00', mi = '00', s = '00'] = m;
  const isUtc = /Z$/.test(val);
  if (isUtc) return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s)).toISOString();
  return new Date(+y, +mo - 1, +d, +h, +mi, +s).toISOString();
}

function unescapeText(s = '') {
  return s.replace(/\\n/g, '\n').replace(/\\,/g, ',').replace(/\\;/g, ';').replace(/\\\\/g, '\\');
}

/**
 * Parse an ICS file into BIDIT events. Imported events are marked
 * `source: 'imported'` and `readOnly: true` (view-only — §4.9).
 */
export function parseIcs(text = '') {
  const events = [];
  const blocks = text.split('BEGIN:VEVENT').slice(1);
  blocks.forEach((block) => {
    const body = block.split('END:VEVENT')[0];
    const get = (key) => {
      const re = new RegExp(`${key}[^:\\r\\n]*:(.+)`);
      const m = body.match(re);
      return m ? m[1].trim() : '';
    };
    const uid = get('UID');
    events.push({
      id: uid || `imported-${events.length}`,
      uid,
      title: unescapeText(get('SUMMARY')) || 'Imported event',
      start: parseIcsDate(get('DTSTART')),
      end: parseIcsDate(get('DTEND')),
      location: unescapeText(get('LOCATION')),
      notes: unescapeText(get('DESCRIPTION')),
      type: 'imported',
      source: 'imported',
      readOnly: true,
    });
  });
  return events;
}

/**
 * Merge imported events into existing ones — re-import UPDATES by UID rather
 * than duplicating (§4.9 acceptance: zero duplicates on re-import).
 */
export function mergeImportedEvents(existing = [], imported = []) {
  const byUid = new Map(existing.map((e) => [e.uid || e.id, e]));
  imported.forEach((e) => {
    byUid.set(e.uid || e.id, { ...byUid.get(e.uid || e.id), ...e });
  });
  return Array.from(byUid.values());
}
